import { ThemeEvents } from '@theme/events';

/**
 * Mobile sticky add-to-cart bar (snippets/sticky-atc.liquid).
 *
 * Appears when the real Add to Cart button has scrolled ABOVE the viewport,
 * hides while the buy box is visible. Its button proxies the real one so the
 * whole add pipeline (cart sections refresh, drawer auto-open, chained
 * "Complete your carry" accessories) runs unchanged. Price / image / disabled
 * state follow variant updates.
 *
 * Visibility is decided from the button's CURRENT position on every
 * scroll/resize frame and after any DOM change in the section — never from
 * IntersectionObserver transitions alone. Two real-world cases broke the
 * transition-only version (Jeff, 2026-09-07):
 *   1. Reload with a restored scroll position (or a fast flick past the
 *      button): the button goes from "below the fold" straight to "above
 *      the fold" without ever intersecting, so no transition fired and the
 *      bar stayed hidden until the next crossing.
 *   2. Colour swap (combined listing): the whole <main> is morphed and the
 *      buy form is REPLACED, so the observer kept watching a detached
 *      button forever — the bar never came back for that page view.
 */
class StickyAtc extends HTMLElement {
  #abort = new AbortController();
  /** @type {IntersectionObserver | undefined} */
  #io;
  /** @type {MutationObserver | undefined} */
  #mo;
  /** @type {HTMLElement | null} */
  #section = null;
  /** @type {HTMLElement | null} */
  #button = null;
  #frame = 0;
  #visible = false;

  connectedCallback() {
    const { signal } = this.#abort;
    this.#section = this.closest('.shopify-section');
    if (!this.#section) return;

    // Re-check on the frame after every scroll/resize (cheap: one rect read).
    window.addEventListener('scroll', this.#requestEvaluate, { passive: true, signal });
    window.addEventListener('resize', this.#requestEvaluate, { passive: true, signal });
    // Page restored from bfcache / tab re-shown: position may have changed.
    window.addEventListener('pageshow', this.#requestEvaluate, { signal });
    document.addEventListener('visibilitychange', this.#requestEvaluate, { signal });

    // A colour swap morphs the section and can replace the buy form; a
    // variant change can re-render pieces of it. Re-bind and re-check.
    this.#mo = new MutationObserver(() => this.#requestEvaluate());
    this.#mo.observe(this.#section, { childList: true, subtree: true });

    this.#applyPreviewOffset();
    setTimeout(() => this.#applyPreviewOffset(), 1500);

    this.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element) || !event.target.closest('[data-sticky-add]')) return;
        this.#findButton()?.click();
      },
      { signal }
    );

    this.#section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    this.#bindObserver();
    this.#evaluate();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#io?.disconnect();
    this.#mo?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  /**
   * The real Add to Cart button, re-queried whenever the cached one has been
   * replaced by a section morph.
   * @returns {HTMLElement | null}
   */
  #findButton() {
    if (this.#button && this.#button.isConnected) return this.#button;
    this.#button = this.#section?.querySelector('product-form-component [ref="addToCartButton"]') ?? null;
    return this.#button;
  }

  /**
   * IntersectionObserver is kept as an extra trigger (it fires without any
   * scroll event when layout shifts push the button around), but the
   * decision itself is always made by #evaluate from the live rect.
   */
  #bindObserver() {
    const button = this.#findButton();
    this.#io?.disconnect();
    if (!button) return;
    this.#io = new IntersectionObserver(() => this.#requestEvaluate());
    this.#io.observe(button);
  }

  #requestEvaluate = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#evaluate();
    });
  };

  #evaluate() {
    const previous = this.#button;
    const button = this.#findButton();
    if (button !== previous) this.#bindObserver();
    if (!button) {
      this.#setVisible(false);
      return;
    }
    // "Scrolled past" = the whole button sits above the viewport.
    const rect = button.getBoundingClientRect();
    const scrolledPast = rect.bottom < 0 && rect.height > 0;
    this.#setVisible(scrolledPast);
  }

  /**
   * On theme previews, Shopify injects a bottom "Draft" bar that would sit
   * exactly on top of this one — lift above it. The modern bar
   * (#PBarNextFrame) is a full-viewport overlay iframe whose visible strip
   * can't be measured cross-origin, so use its known ~68px height. No-op on
   * the live theme. Re-applied on every evaluation because a section morph
   * copies the server's attributes over ours and drops the inline style
   * (seen on the iOS Simulator: bar "missing" after a colour swap — it was
   * behind the Draft bar).
   */
  #applyPreviewOffset() {
    if (document.querySelector('#PBarNextFrame, #preview-bar-iframe')) {
      if (this.style.bottom !== '68px') this.style.bottom = '68px';
    }
  }

  /** @param {boolean} visible */
  #setVisible(visible) {
    // Always sync the DOM: a section morph copies the server's attributes
    // (no is-visible, aria-hidden="true", no inline style) over ours, so the
    // cached flag can be true while the element is hidden.
    this.#applyPreviewOffset();
    this.classList.toggle('is-visible', visible);
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');
    if (visible !== this.#visible) {
      this.#visible = visible;
      this.#liftChatBubble(visible);
    }
  }

  /**
   * Ride the Gorgias chat bubble up with the bar (it sits bottom-right,
   * exactly over the Add button) and settle it back when the bar hides.
   * Queried per call — Gorgias injects late.
   * @param {boolean} lift
   */
  #liftChatBubble(lift) {
    // Target the fixed bubble iframe itself — transforming its wrapper
    // (#gorgias-chat-container) would turn the wrapper into a containing
    // block and yank the fixed bubble into page flow.
    const chat = document.querySelector('iframe#chat-button');
    if (!chat) return;

    // desktop: the bar is display:none — never move the bubble there
    if (this.offsetHeight === 0) lift = false;

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      chat.style.transition = 'transform 0.25s ease';
    }
    if (lift) {
      const previewOffset = parseInt(this.style.bottom, 10) || 0;
      chat.style.transform = `translateY(-${this.offsetHeight + previewOffset + 8}px)`;
    } else {
      chat.style.transform = '';
    }
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    const code = this.dataset.currencyCode || '';
    const symbol = this.dataset.currencySymbol || '$';
    const zeroDecimal = ['JPY', 'KRW', 'TWD', 'VND'].includes(code);
    const amount = zeroDecimal ? String(Math.round(cents)) : (cents / 100).toFixed(2).replace(/\.00$/, '');
    return symbol + amount;
  }

  /** @param {Event & {detail?: any}} event */
  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (!variant) return;

    const price = this.querySelector('[data-sticky-price]');
    if (price && typeof variant.price === 'number') price.textContent = this.#formatMoney(variant.price);

    const button = this.querySelector('[data-sticky-add]');
    if (button) button.disabled = variant.available === false;

    const name = this.querySelector('[data-sticky-variant]');
    let title = typeof variant.title === 'string' ? variant.title : variant.public_title;
    if (name && typeof title === 'string') {
      // Bundles: collapse "A / A / A" -> "A set", mixed -> "Mixed set" (mirrors
      // the Liquid in sticky-atc.liquid and the collection cards).
      if (this.dataset.bundle === 'true' && title.includes(' / ')) {
        const parts = title.split(' / ');
        const setTpl = this.dataset.setLabelTemplate || '__COLOUR__ set';
        const mixedLabel = this.dataset.mixedSetLabel || 'Mixed set';
        title = parts.every((p) => p === parts[0]) ? setTpl.replace('__COLOUR__', parts[0]) : mixedLabel;
      }
      name.textContent = title;
      name.hidden = title === 'Default Title' || title === '';
    }

    const imageSrc = variant.featured_media?.preview_image?.src;
    const img = this.querySelector('[data-sticky-img]');
    if (img && imageSrc) img.src = imageSrc + '&width=96';

    this.#requestEvaluate();
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
